import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Download, Eye, AlertCircle, RefreshCw, CheckSquare, Square, Archive, ChevronLeft, ChevronRight, X, Trash2 } from 'lucide-react';
import { ConfirmationDialog } from './ui/ConfirmationDialog';

interface Photo {
  id: string;
  url: string;
  fileName: string;
  originalName: string;
}

interface PhotoGalleryProps {
  isAdmin?: boolean;
  hideHeader?: boolean;
  showHoverIcon?: boolean;
  filterPhotoIds?: Set<string>;
  onPhotosLoaded?: (photos: Photo[]) => void;
  autoRefreshMs?: number;
}

export const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  isAdmin = false,
  hideHeader = false,
  showHoverIcon = true,
  filterPhotoIds,
  onPhotosLoaded,
  autoRefreshMs,
}) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [modalPhotos, setModalPhotos] = useState<Photo[]>([]);
  const [currentModalIndex, setCurrentModalIndex] = useState(0);
  const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set());
  const [deletingPhotos, setDeletingPhotos] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'single' | 'bulk'; photoId?: string }>({ type: 'single' });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const isMobile = /iPhone|iPad|iPod|Android|Mobile/i.test(navigator.userAgent);
  const [showBulkProgress, setShowBulkProgress] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ total: number; completed: number; phase: 'idle' | 'preparing' | 'downloading'; }>({ total: 0, completed: 0, phase: 'idle' });
  const progressPollRef = useRef<number | null>(null);
  const progressJobIdRef = useRef<string | null>(null);

  const generateJobId = () => `zip_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const startProgressPolling = (jobId: string) => {
    progressJobIdRef.current = jobId;
    if (progressPollRef.current) {
      window.clearInterval(progressPollRef.current);
      progressPollRef.current = null;
    }
    const id = window.setInterval(async () => {
      try {
        const resp = await fetch(`/api/photos-zip-progress?jobId=${encodeURIComponent(jobId)}`);
        if (!resp.ok) return;
        const data = await resp.json();
        if (data && typeof data.total === 'number' && typeof data.completed === 'number') {
          setBulkProgress({ total: data.total, completed: data.completed, phase: data.phase === 'downloading' ? 'downloading' : 'preparing' });
          if (data.done) {
            // Stop polling when server marks done
            if (progressPollRef.current) {
              window.clearInterval(progressPollRef.current);
              progressPollRef.current = null;
            }
          }
        }
      } catch {
        // ignore transient errors
      }
    }, 500);
    progressPollRef.current = id as unknown as number;
  };

  const stopProgressPolling = () => {
    if (progressPollRef.current) {
      window.clearInterval(progressPollRef.current);
      progressPollRef.current = null;
    }
    progressJobIdRef.current = null;
  };

  const fetchPhotos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/photos');
      const result = await response.json();
      
      if (result.success) {
        const loadedPhotos: Photo[] = result.photos || [];
        setPhotos(loadedPhotos);
        onPhotosLoaded?.(loadedPhotos);
      } else {
        setError(result.error || 'Failed to load photos');
      }
    } catch (err) {
      // Handle error silently
      setError('Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [onPhotosLoaded]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  useEffect(() => {
    if (!autoRefreshMs) return;
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') fetchPhotos();
    }, autoRefreshMs);
    return () => window.clearInterval(intervalId);
  }, [autoRefreshMs, fetchPhotos]);

  const displayPhotos = filterPhotoIds
    ? photos.filter((photo) => filterPhotoIds.has(photo.id))
    : photos;

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!selectedPhoto || modalPhotos.length === 0) return;
      
      switch (e.key) {
        case 'Escape':
          setSelectedPhoto(null);
          setModalPhotos([]);
          setCurrentModalIndex(0);
          break;
        case 'ArrowLeft':
          if (modalPhotos.length > 1) {
            const newIndex = currentModalIndex > 0 ? currentModalIndex - 1 : modalPhotos.length - 1;
            setCurrentModalIndex(newIndex);
            const next = modalPhotos[newIndex];
            if (next) setSelectedPhoto(next);
          }
          break;
        case 'ArrowRight':
          if (modalPhotos.length > 1) {
            const newIndex = currentModalIndex < modalPhotos.length - 1 ? currentModalIndex + 1 : 0;
            setCurrentModalIndex(newIndex);
            const next = modalPhotos[newIndex];
            if (next) setSelectedPhoto(next);
          }
          break;
      }
    };

    if (selectedPhoto) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [selectedPhoto, modalPhotos, currentModalIndex]);

  const handleSelectPhoto = (photoId: string) => {
    const newSelected = new Set(selectedPhotos);
    if (newSelected.has(photoId)) {
      newSelected.delete(photoId);
    } else {
      newSelected.add(photoId);
    }
    setSelectedPhotos(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedPhotos.size === displayPhotos.length) {
      setSelectedPhotos(new Set());
    } else {
      setSelectedPhotos(new Set(displayPhotos.map(photo => photo.id)));
    }
  };

  const handleImageLoad = (photoId: string) => {
    setLoadingImages(prev => {
      const newSet = new Set(prev);
      newSet.delete(photoId);
      return newSet;
    });
  };

  const handleImageLoadStart = (photoId: string) => {
    setLoadingImages(prev => new Set(prev).add(photoId));
  };

  const handleSingleDownload = async (photo: Photo) => {
    try {
      const res = await fetch(photo.url, { mode: 'cors' });
      if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
      const blob = await res.blob();
      const fileName = photo.originalName || 'photo';

      const navAny = navigator as any;
      if (isMobile && navAny?.share && navAny?.canShare) {
        const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
        if (navAny.canShare({ files: [file] })) {
          await navAny.share({ files: [file], title: 'Wedding Photo' });
          return;
        }
      }

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
    } catch (e) {
      window.open(photo.url, '_blank');
    }
  };

  const handleBulkDownload = async () => {
    if (selectedPhotos.size === 0) return;
    
    try {
      setBulkDownloading(true);
      const selectedPhotosList = photos.filter(photo => selectedPhotos.has(photo.id));
      setShowBulkProgress(true);
      setBulkProgress({ total: isMobile ? selectedPhotosList.length : 0, completed: 0, phase: isMobile ? 'downloading' : 'preparing' });
      
      if (!isMobile) {
        // Desktop: request a single ZIP from the server
        const ids = selectedPhotosList.map(p => p.id);
        const jobId = generateJobId();
        startProgressPolling(jobId);
        const res = await fetch('/api/photos-zip', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids, jobId }),
        });
        if (!res.ok) throw new Error(`Failed to create zip: ${res.status}`);
        setBulkProgress(prev => ({ ...prev, phase: 'downloading' }));
        const zipBlob = await res.blob();
        const zipUrl = URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = zipUrl;
        link.download = `wedding-photos-${selectedPhotosList.length}.zip`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(zipUrl), 2000);
      } else {
        // Mobile fallback (UI hides bulk on mobile, but keep safe behavior)
        for (const photo of selectedPhotosList) {
          try {
            const res = await fetch(photo.url, { mode: 'cors' });
            if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
            const blob = await res.blob();
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = photo.originalName || 'photo';
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
            setBulkProgress(prev => ({ ...prev, completed: Math.min(prev.completed + 1, prev.total) }));
            await new Promise(resolve => setTimeout(resolve, 150));
          } catch (e) {
            // Continue with next photo on individual failure
          }
        }
      }
      
      setSelectedPhotos(new Set());
    } catch (err) {
      // Handle error silently
      setError('Failed to download photos');
    } finally {
      setBulkDownloading(false);
      stopProgressPolling();
      setShowBulkProgress(false);
      setBulkProgress({ total: 0, completed: 0, phase: 'idle' });
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    try {
      setDeletingPhotos(prev => new Set(prev).add(photoId));
      
      const response = await fetch(`/api/photos?photoId=${photoId}`, {
        method: 'DELETE',
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Remove photo from state
        setPhotos(prev => prev.filter(photo => photo.id !== photoId));
        setSelectedPhotos(prev => {
          const newSet = new Set(prev);
          newSet.delete(photoId);
          return newSet;
        });
        
        // Close modal if deleted photo was being viewed
        if (selectedPhoto?.id === photoId) {
          setSelectedPhoto(null);
          setModalPhotos([]);
          setCurrentModalIndex(0);
        }
      } else {
        setError(result.error || 'Failed to delete photo');
      }
    } catch (err) {
      // Handle error silently
      setError('Failed to delete photo');
    } finally {
      setDeletingPhotos(prev => {
        const newSet = new Set(prev);
        newSet.delete(photoId);
        return newSet;
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPhotos.size === 0) return;
    
    try {
      setDeleteLoading(true);
      const selectedPhotosList = Array.from(selectedPhotos);
      const failedDeletions: string[] = [];
      
      // Delete each selected photo
      for (const photoId of selectedPhotosList) {
        try {
          const response = await fetch(`/api/photos?photoId=${photoId}`, {
            method: 'DELETE',
          });
          
          const result = await response.json();
          
          if (!result.success) {
            failedDeletions.push(photoId);
          }
        } catch (err) {
          // Handle error silently
          failedDeletions.push(photoId);
        }
      }
      
      // Update photos state by removing successfully deleted photos
      const successfullyDeleted = selectedPhotosList.filter(id => !failedDeletions.includes(id));
      setPhotos(prev => prev.filter(photo => !successfullyDeleted.includes(photo.id)));
      setSelectedPhotos(new Set());
      
      // Show error if some deletions failed
      if (failedDeletions.length > 0) {
        setError(`Failed to delete ${failedDeletions.length} photo${failedDeletions.length > 1 ? 's' : ''}`);
      }
      
      // Close modal if any viewed photos were deleted
      if (modalPhotos.some(photo => successfullyDeleted.includes(photo.id))) {
        setSelectedPhoto(null);
        setModalPhotos([]);
        setCurrentModalIndex(0);
      }
    } catch (err) {
      // Handle error silently
      setError('Failed to delete photos');
    } finally {
      setDeleteLoading(false);
    }
  };

  const confirmDelete = () => {
    if (deleteTarget.type === 'single' && deleteTarget.photoId) {
      handleDeletePhoto(deleteTarget.photoId);
    } else if (deleteTarget.type === 'bulk') {
      handleBulkDelete();
    }
    setShowDeleteConfirm(false);
  };

  const initiateDelete = (type: 'single' | 'bulk', photoId?: string) => {
    setDeleteTarget({ type, photoId });
    setShowDeleteConfirm(true);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 text-blue-600 mx-auto mb-3 animate-spin" />
          <p className="text-gray-600">Loading photos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-3" />
          <p className="text-red-600 mb-3">{error}</p>
          <button
            onClick={fetchPhotos}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again.
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="p-6">
        {/* Header with bulk actions */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 space-y-4 sm:space-y-0">
          {!hideHeader && (
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Wedding Photos</h2>
              <p className="text-gray-600">
                {displayPhotos.length} photos
                {selectedPhotos.size > 0 && ` • ${selectedPhotos.size} selected`}
              </p>
            </div>
          )}
          {displayPhotos.length > 0 && !isMobile && (
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleSelectAll}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {selectedPhotos.size === displayPhotos.length ? (
                  <CheckSquare className="h-4 w-4" />
                ) : (
                  <Square className="h-4 w-4" />
                )}
                <span>
                  {selectedPhotos.size === displayPhotos.length ? 'Deselect All' : 'Select All'}
                </span>
              </button>
              
              {selectedPhotos.size > 0 && (
                <>
                  <button
                    onClick={handleBulkDownload}
                    disabled={bulkDownloading}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Archive className="h-4 w-4" />
                    <span>
                      {bulkDownloading ? 'Downloading...' : `Download ${selectedPhotos.size} Photo${selectedPhotos.size > 1 ? 's' : ''}`}
                    </span>
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => initiateDelete('bulk')}
                      disabled={deleteLoading}
                      className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>
                        {deleteLoading ? 'Deleting...' : `Delete ${selectedPhotos.size} Photo${selectedPhotos.size > 1 ? 's' : ''}`}
                      </span>
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {displayPhotos.length === 0 ? (
          <div className="text-center py-12">
            {showHoverIcon && !hideHeader && (
              <Eye className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            )}
            <p className="text-gray-600">No photos have been uploaded yet.</p>
            {!hideHeader && (
              <p className="text-sm text-gray-400 mt-1">Photos uploaded by guests will appear here.</p>
            )}
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 xl:columns-5 gap-4 md:gap-6" style={{ columnFill: 'balance' }}>
            {displayPhotos.map((photo) => (
              <div
                key={photo.id}
                className="bg-gray-50 rounded-lg overflow-hidden relative group break-inside-avoid mb-4 md:mb-6 shadow-sm hover:shadow-md transition-shadow duration-300 cursor-zoom-in touch-manipulation"
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (isAdmin && selectedPhotos.size > 0) {
                    const selectedPhotosList = displayPhotos.filter(p => selectedPhotos.has(p.id));
                    const index = selectedPhotosList.findIndex(p => p.id === photo.id);
                    const startIndex = index >= 0 ? index : 0;
                    setModalPhotos(selectedPhotosList);
                    setCurrentModalIndex(startIndex);
                    const picked = selectedPhotosList[startIndex] || photo;
                    setSelectedPhoto(picked);
                  } else {
                    setModalPhotos([photo]);
                    setCurrentModalIndex(0);
                    setSelectedPhoto(photo);
                  }
                }}
                onPointerUp={(e) => {
                  // Avoid duplicate open when interacting with controls
                  if ((e.target as HTMLElement).closest('a,button')) return;
                  (e.currentTarget as HTMLElement).click();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    (e.currentTarget as HTMLElement).click();
                  }
                }}
              >
                {/* Selection checkbox */}
                {!isMobile && (
                  <div className="absolute top-2 left-2 z-10">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSelectPhoto(photo.id); }}
                      className="bg-white bg-opacity-90 hover:bg-opacity-100 rounded p-1 transition-all shadow-sm"
                    >
                      {selectedPhotos.has(photo.id) ? (
                        <CheckSquare className="h-5 w-5 text-blue-600" />
                      ) : (
                        <Square className="h-5 w-5 text-gray-600" />
                      )}
                    </button>
                  </div>
                )}
                
                {/* Action buttons */}
                <div className="absolute top-2 right-2 z-10 flex space-x-1">
                  <button
                    onClick={async (e) => { e.stopPropagation(); await handleSingleDownload(photo); }}
                    className="bg-white bg-opacity-90 hover:bg-opacity-100 rounded p-1 transition-all shadow-sm inline-block"
                  >
                    <Download className="h-5 w-5 text-gray-600 hover:text-blue-600" />
                  </button>
                  {isAdmin && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        initiateDelete('single', photo.id);
                      }}
                      disabled={deletingPhotos.has(photo.id)}
                      className="bg-white bg-opacity-90 hover:bg-opacity-100 rounded p-1 transition-all shadow-sm disabled:opacity-50"
                    >
                      {deletingPhotos.has(photo.id) ? (
                        <RefreshCw className="h-5 w-5 text-gray-600 animate-spin" />
                      ) : (
                        <Trash2 className="h-5 w-5 text-gray-600 hover:text-red-600" />
                      )}
                    </button>
                  )}
                </div>
                
                <div className="relative">
                  {loadingImages.has(photo.id) && (
                    <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
                      <RefreshCw className="h-6 w-6 text-gray-400 animate-spin" />
                    </div>
                  )}
                  <img
                    src={photo.url}
                    alt="Wedding photo"
                    className="w-full h-auto object-contain transition-all duration-300 group-hover:scale-[1.02]"
                    style={{ display: loadingImages.has(photo.id) ? 'none' : 'block' }}
                    onLoadStart={() => handleImageLoadStart(photo.id)}
                    onLoad={() => handleImageLoad(photo.id)}
                    onError={() => handleImageLoad(photo.id)}
                  />
                  
                  {showHoverIcon && (
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity flex items-center justify-center pointer-events-none">
                      <Eye className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {showBulkProgress && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg shadow-md px-5 py-4 w-80">
            <div className="text-gray-900 font-medium mb-2">
              {bulkProgress.phase === 'preparing' ? 'Preparing download…' : 'Downloading photos…'}
            </div>
            <div className="w-full h-2 bg-gray-200 rounded overflow-hidden">
              {bulkProgress.phase === 'downloading' && bulkProgress.total > 0 ? (
                <div
                  className="h-2 bg-blue-600 transition-all"
                  style={{ width: `${Math.round((bulkProgress.completed / Math.max(bulkProgress.total, 1)) * 100)}%` }}
                />
              ) : (
                <div className="h-2 w-full bg-blue-600 animate-pulse" />
              )}
            </div>
            <div className="text-sm text-gray-600 mt-2">
              {bulkProgress.phase === 'downloading' && bulkProgress.total > 0
                ? `${bulkProgress.completed} / ${bulkProgress.total}`
                : 'This may take a moment.'}
            </div>
          </div>
        </div>
      )}

      {/* Photo Modal with Slideshow */}
      {selectedPhoto && modalPhotos.length > 0 && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50" style={{zIndex: 9999}}>
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close Button */}
            <button
              onClick={() => {
                setSelectedPhoto(null);
                setModalPhotos([]);
                setCurrentModalIndex(0);
              }}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-20 bg-black bg-opacity-50 rounded-full p-2 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Previous Button */}
            {modalPhotos.length > 1 && (
              <button
                onClick={() => {
                  const newIndex = currentModalIndex > 0 ? currentModalIndex - 1 : modalPhotos.length - 1;
                  setCurrentModalIndex(newIndex);
                  setSelectedPhoto(modalPhotos[newIndex]);
                }}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-20 bg-black bg-opacity-50 rounded-full p-3 transition-colors"
              >
                <ChevronLeft className="h-8 w-8" />
              </button>
            )}

            {/* Next Button */}
            {modalPhotos.length > 1 && (
              <button
                onClick={() => {
                  const newIndex = currentModalIndex < modalPhotos.length - 1 ? currentModalIndex + 1 : 0;
                  setCurrentModalIndex(newIndex);
                  setSelectedPhoto(modalPhotos[newIndex]);
                }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-20 bg-black bg-opacity-50 rounded-full p-3 transition-colors"
              >
                <ChevronRight className="h-8 w-8" />
              </button>
            )}

            {/* Main Image */}
            <div className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center">
              <img
                src={modalPhotos[currentModalIndex]?.url}
                alt="Wedding photo"
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              />
            </div>

            {/* Image Counter */}
            {modalPhotos.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white bg-black bg-opacity-50 px-4 py-2 rounded-full">
                <span className="text-sm">
                  {currentModalIndex + 1} of {modalPhotos.length}
                </span>
              </div>
            )}

            {/* Download Button */}
            <div className="absolute bottom-4 right-4 z-20">
              <button
                onClick={async () => {
                  const current = modalPhotos[currentModalIndex];
                  if (current) await handleSingleDownload(current);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Download</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Confirmation Dialog (admin only) */}
      {isAdmin && (
        <ConfirmationDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={confirmDelete}
          title={deleteTarget.type === 'bulk' ? 'Delete Multiple Photos' : 'Delete Photo'}
          message={
            deleteTarget.type === 'bulk'
              ? `Are you sure you want to delete ${selectedPhotos.size} photo${selectedPhotos.size > 1 ? 's' : ''}? This action cannot be undone.`
              : 'Are you sure you want to delete this photo? This action cannot be undone.'
          }
          confirmText="Delete"
          cancelText="Cancel"
          isDestructive={true}
          isLoading={deleteLoading || (deleteTarget.photoId ? deletingPhotos.has(deleteTarget.photoId) : false)}
        />
      )}
    </div>
  );
};
